import React from 'react';
import GeneralChart from '@/components/city/GeneralChart';
import { CusEcharts } from '@/components/AsyncComponent';
import type { ECOption } from '@/components/CusEcharts';
import { CityContext } from '@/context/create-context';
import { useClintMediaQuery } from '@/utils/hooks';
import { TAB_INDUSTRY_RATIO } from '@/constants/city';

const DEFAULT_COLORS = [
  '#5470c6',
  '#91cc75',
  '#fac858',
  '#ee6666',
  '#73c0de',
  '#3ba272',
  '#fc8452',
  '#9a60b4',
  '#ea7ccc',
];

const IndustryRatio = () => {
  const chartRef = React.useRef(null);
  const { name, market_share: marketShare } = React.useContext(CityContext);
  const isMobile = useClintMediaQuery();

  const rangeData = React.useMemo(() => {
    const totalCount = marketShare
      .map(({ count }) => count)
      .reduce((a, b) => a + b, 0);

    return marketShare.map(({ name, id, count }) => {
      return { name, id, ratio: Math.round((count / totalCount) * 100) };
    });
  }, [marketShare]);

  const config = React.useMemo<ECOption>(() => {
    const seoInfo = `${name}哪些行业的招聘需求多呢？截止至当前统计，${name}${rangeData
      .map(
        ({ name, ratio }) => `${name}行业招聘需求占比整个招聘市场的${ratio}%`,
      )
      .join('，')}。本数据统计来源于各大平台发布的公开数据，仅供参考。`;

    return {
      legend: {
        show: false,
      },
      grid: {
        show: false,
        tooltip: { show: false },
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
      },
      series: [
        {
          type: 'pie',
          silent: true,
          radius: isMobile ? [20, 110] : [20, 140],
          roseType: 'radius',
          center: ['50%', '50%'],
          itemStyle: {
            borderRadius: 5,
          },
          label: {
            show: true,
            backgroundColor: 'transparent',
            bleedMargin: 1,
          },
          labelLine: { length: 0 },
          emphasis: {
            disabled: true,
            label: {
              show: false,
            },
          },
          cursor: 'default',
          data: rangeData.map(({ name, ratio }) => {
            return { name, value: ratio };
          }),
        },
      ],
      seoInfo,
    };
  }, [rangeData, isMobile]);

  return (
    <GeneralChart
      title={`${name}招聘行业市场占比`}
      seoInfo={config.seoInfo}
      dataEleGroup={TAB_INDUSTRY_RATIO}
    >
      <div className="tablet:justify-between tablet:flex">
        <div className="tablet:w-7/12">
          <div className="w-full h-[290px]">
            <CusEcharts chartRef={chartRef} config={config} />
          </div>
        </div>
        <div className="pt-2 flex flex-col gap-y-2.5 text-sm leading-5.5 text-black/85 tablet:">
          {rangeData.map(({ name, ratio }, index) => (
            <p key={index} className="bg-white px-8 py-1.5 rounded-lg flex">
              <span
                className="text-sm leading-5 px-2 rounded-full"
                style={{ backgroundColor: DEFAULT_COLORS[index] }}
              >
                {index + 1}
              </span>
              <span className="mx-2 h-[22px] line-clamp-1">{name}</span>
              <span className="text-left ml-auto h-[22px] line-clamp-1">{`${ratio}%`}</span>
            </p>
          ))}
        </div>
      </div>
    </GeneralChart>
  );
};

export default IndustryRatio;
